import type { Player, Team } from '../types';
import type { Rng } from './rng';

// Todo consumo de aleatoriedade do arquivo passa por um Rng recebido de fora.
// O default continua sendo Math.random, então quem não passa nada mantém o
// comportamento do mobile; o sorteio da web passa um gerador semeado, e é isso
// que torna o resultado reproduzível a partir do seed publicado.
const defaultRng: Rng = Math.random;

export function shuffle<T>(arr: T[], rng: Rng): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function addToTeam(teams: Team[], idx: number, player: Player): void {
  teams[idx].players.push(player);
  teams[idx].totalStars += player.level;
}

// Among a list of candidates, picks the one with:
//   1. Lowest totalStars (primary — maximises star balance)
//   2. Fewest players    (tie-breaker — evens out team sizes)
//   3. Random            (among fully tied candidates — removes index bias)
function pickBest(candidates: { i: number; stars: number; count: number }[], rng: Rng): number {
  const minStars = Math.min(...candidates.map(x => x.stars));
  const atMinStars = candidates.filter(x => x.stars === minStars);

  const minCount = Math.min(...atMinStars.map(x => x.count));
  const atMinBoth = atMinStars.filter(x => x.count === minCount);

  return atMinBoth[Math.floor(rng() * atMinBoth.length)].i;
}

// Ordena por estrelas asc, depois por tamanho asc, e desempata por sorteio.
//
// A chave de desempate é sorteada ANTES de ordenar, e não dentro do
// comparador. Um comparador que devolve `rng() - 0.5` é inconsistente: o
// resultado passa a depender de quantas comparações o algoritmo de ordenação
// faz e em que ordem — TimSort (V8) e o merge sort do JavaScriptCore (Safari)
// chegam a saídas diferentes com o mesmo seed. Isso quebraria a verificação de
// sorteio da Fatia 9 para quem auditasse pelo iPhone. Com a chave sorteada de
// antemão o comparador vira consistente e a saída deixa de depender do motor.
type Eligible = { i: number; stars: number; count: number; tie: number };

function sortEligible(candidates: Omit<Eligible, 'tie'>[], rng: Rng): Eligible[] {
  return candidates
    .map(c => ({ ...c, tie: rng() }))
    .sort((a, b) => {
      if (a.stars !== b.stars) return a.stars - b.stars;
      if (a.count !== b.count) return a.count - b.count;
      return a.tie - b.tie;
    });
}

// Returns the index of the best team to receive the next player.
//
// Rule 1 (GOLD): main teams (0..numTeams-1) are checked first.
//   The overflow team(s) only become eligible once ALL main teams are full.
//   This guarantees: Time 1…N fill to playersPerTeam before Time N+1 receives anyone.
//
// Rule 2: among eligible teams, pick by lowest totalStars (see pickBest).
//
// Fallback: if every team is somehow full (impossible with Math.ceil, kept as safety net),
//   add to the globally lowest-sum team.
function getEligibleTeam(teams: Team[], numTeams: number, playersPerTeam: number, rng: Rng): number {
  const toCandidates = (slice: Team[], offset: number) =>
    slice
      .map((t, li) => ({ i: li + offset, stars: t.totalStars, count: t.players.length }))
      .filter(x => x.count < playersPerTeam);

  const mainCandidates = toCandidates(teams.slice(0, numTeams), 0);
  if (mainCandidates.length > 0) return pickBest(mainCandidates, rng);

  const overflowCandidates = toCandidates(teams.slice(numTeams), numTeams);
  if (overflowCandidates.length > 0) return pickBest(overflowCandidates, rng);

  return teams.reduce(
    (minI, t, i, arr) => (t.totalStars < arr[minI].totalStars ? i : minI),
    0,
  );
}

// Strongest-first ordering with same-level players shuffled to randomize ties.
// Works for any positive numeric level (0.5 increments included).
function sortByLevelDesc(players: Player[], rng: Rng): Player[] {
  const groups = new Map<number, Player[]>();
  players.forEach(p => {
    const arr = groups.get(p.level);
    if (arr) arr.push(p);
    else groups.set(p.level, [p]);
  });
  const levels = [...groups.keys()].sort((a, b) => b - a);
  return levels.flatMap(lvl => shuffle(groups.get(lvl)!, rng));
}

function recomputeStars(team: Team): void {
  team.totalStars = team.players.reduce((s, p) => s + p.level, 0);
}

/**
 * Sorteia a ordem dentro de cada time. É o ÚLTIMO passo do sorteio.
 *
 * O balanceador distribui do mais forte ao mais fraco, e essa ordem de inserção
 * ficava sendo a ordem de exibição — no servidor ela ainda vira
 * `draw_team_player.position`, então saía gravada. Efeito: a lista do time é o
 * ranking de habilidade do time, de cima para baixo. Quem organiza vira o
 * celular para a roda e entrega, sem dizer uma palavra, quem ele considera o
 * pior jogador ali — que é exatamente o que o botão de olho existe para evitar,
 * e ele não protegia nada contra isto porque a informação está na ORDEM, não no
 * número.
 *
 * O mobile já fazia: `shuffled(team.players)` na tela e no compartilhar. A web
 * nunca portou.
 *
 * Aqui, e não na tela, por dois motivos. A ordem tem de ser sorteada UMA vez —
 * no mobile ela é sorteada a cada renderização, e a lista se reembaralha quando
 * qualquer coisa muda na tela. E é o mesmo sorteio que precisa valer no
 * compartilhar, no que fica gravado e na revelação nome por nome.
 *
 * Não toca em `totalStars`: a soma não depende da ordem.
 */
function shuffleWithinTeams(teams: Team[], rng: Rng): Team[] {
  return teams.map(team => ({ ...team, players: shuffle(team.players, rng) }));
}

// Distributes a single-gender group across teams using a strict round-robin:
// in each round, every team with capacity receives ONE player, picked
// strongest-first and routed to the team with the lowest current totalStars.
// This guarantees that the number of players from this group differs by at
// most 1 between any two teams that had capacity throughout the placement.
//
// Main teams are preferred over overflow until they all reach playersPerTeam.
function distributeGenderGroup(
  teams: Team[],
  group: Player[],
  numTeams: number,
  playersPerTeam: number,
  rng: Rng,
): void {
  if (group.length === 0) return;
  const sorted = sortByLevelDesc(group, rng);
  let cursor = 0;

  while (cursor < sorted.length) {
    const mainElig = teams.slice(0, numTeams)
      .map((t, i) => ({ i, stars: t.totalStars, count: t.players.length }))
      .filter(c => c.count < playersPerTeam);

    const ovElig = teams.slice(numTeams)
      .map((t, idx) => ({ i: idx + numTeams, stars: t.totalStars, count: t.players.length }))
      .filter(c => c.count < playersPerTeam);

    const candidates = mainElig.length > 0 ? mainElig : ovElig;
    if (candidates.length === 0) break;

    // Stars asc, then count asc, then random tie-break — guarantees the
    // strongest remaining player goes to the team most in need of stars
    // while keeping team sizes even across rounds.
    const elig = sortEligible(candidates, rng);

    for (const c of elig) {
      if (cursor >= sorted.length) break;
      addToTeam(teams, c.i, sorted[cursor++]);
    }
  }
}

// Rule: for the gender-balanced draw, a player without a stored gender is
// treated as male. So the relevant split is F vs everyone-else.
function isFemale(p: Player): boolean {
  return p.gender === 'F';
}

// Belt-and-suspenders safety net: after the round-robin distribution and the
// optimizer pass, if the F count differs by more than 1 across main teams,
// swap an F from the surplus team with a non-F from the deficit team. The
// non-F count is balanced as a side effect, since total per team is fixed.
function enforceGenderBalance(teams: Team[], numTeams: number): void {
  const main = teams.slice(0, numTeams);
  let guard = 0;
  while (guard++ < 100) {
    const counts = main.map(t => t.players.filter(isFemale).length);
    const maxC = Math.max(...counts);
    const minC = Math.min(...counts);
    if (maxC - minC <= 1) break;
    const maxIdx = counts.indexOf(maxC);
    const minIdx = counts.indexOf(minC);
    const fIdx = main[maxIdx].players.findIndex(isFemale);
    const nonFIdx = main[minIdx].players.findIndex(p => !isFemale(p));
    if (fIdx === -1 || nonFIdx === -1) break;

    const a = main[maxIdx].players[fIdx];
    const b = main[minIdx].players[nonFIdx];
    main[maxIdx].players[fIdx] = b;
    main[minIdx].players[nonFIdx] = a;
    main[maxIdx].totalStars = main[maxIdx].totalStars - a.level + b.level;
    main[minIdx].totalStars = main[minIdx].totalStars - b.level + a.level;
  }
}

function applyGenderRoundRobin(
  teams: Team[],
  present: Player[],
  numTeams: number,
  playersPerTeam: number,
  rng: Rng,
): void {
  const females = present.filter(isFemale);
  // Everyone else (stored M plus unspecified) goes into one bucket per the
  // "treat sem-gênero como homem" rule.
  const others  = present.filter(p => !isFemale(p));

  // Spread the smaller bucket first so the minority lands evenly before
  // capacity is eaten by the larger group.
  const groups = [females, others].sort((a, b) => a.length - b.length);
  groups.forEach(g => distributeGenderGroup(teams, g, numTeams, playersPerTeam, rng));
}

export interface RematchOptions {
  balanceByGender?: boolean;
  rng?: Rng;
}

// Same idea as pickBest, but each team has its own per-team capacity. Used by
// the rematch flow where the two teams may have started with different sizes
// (e.g. main 5 + overflow 1) and we want to preserve that.
function pickBestWithCaps(teams: Team[], caps: number[], rng: Rng): number {
  const candidates = teams
    .map((t, i) => ({ i, stars: t.totalStars, count: t.players.length }))
    .filter(c => c.count < caps[c.i]);
  if (candidates.length === 0) {
    return teams.reduce(
      (minI, t, i, arr) => t.totalStars < arr[minI].totalStars ? i : minI,
      0,
    );
  }
  return pickBest(candidates, rng);
}

/**
 * Distribui um grupo entre times com capacidades DIFERENTES entre si.
 *
 * ⚠️ **Compara nota projetada (soma ÷ vagas), e não soma crua.** É a mesma
 * cegueira de tamanho que o `gap` do `suggestSwaps` documenta desde 30/07: a
 * soma não é comparável entre times de tamanhos diferentes. Equalizando somas,
 * um time de 3 fica "igual" a um de 6 com metade dos jogadores — ou seja, com o
 * dobro da nota por jogador. Medido em 400 sorteios de 15 em 3×6, o time curto
 * nascia +0,95 acima dos cheios; pela projeção, +0,16.
 *
 * ⚠️ **Um jogador por vez, e não uma rodada inteira.** Distribuir a rodada toda
 * antes de recalcular é o que fechava o time de 3 nas três primeiras rodadas —
 * quando só os 9 mais fortes tinham sido distribuídos. Ele nunca chegava às
 * rodadas fracas. Recalculando a cada jogador, a projeção do time curto sobe
 * rápido (cada estrela vale o dobro lá dentro) e ele sai da frente da fila.
 *
 * O tamanho de cada time NÃO muda com isso: quem manda nele é `caps`, que vem
 * de fora. O que muda é QUEM cai em cada vaga.
 */
function distributeRoundRobinWithCaps(teams: Team[], group: Player[], caps: number[], rng: Rng): void {
  if (group.length === 0) return;

  for (const player of sortByLevelDesc(group, rng)) {
    // O filtro vem antes da conta: `caps` pode trazer zero (13 em 4 times de 5
    // dá [5, 5, 3, 0]), e dividir por ele produziria um Infinity que só some
    // porque o time já estava cheio de qualquer forma.
    const candidates = teams
      .map((t, i) => ({ t, i }))
      .filter(({ t, i }) => t.players.length < caps[i])
      .map(({ t, i }) => ({ i, stars: t.totalStars / caps[i], count: t.players.length }));
    if (candidates.length === 0) break;

    // A ordenação é a mesma de sempre — projeção asc, tamanho asc, sorteio no
    // empate. Só o primeiro colocado é usado, porque a próxima escolha precisa
    // enxergar este jogador já colocado.
    addToTeam(teams, sortEligible(candidates, rng)[0].i, player);
  }
}

// Redistributes the combined players of two teams back into those same two
// teams, **preserving the original team sizes** (e.g. 5+1 stays 5+1, not 3+3).
// Identities (id, name) are kept; stars rebalance from scratch.
// When `balanceByGender` is on, distribution is gender-aware and the optimizer
// only swaps players within the same group (F vs everyone-else).
//
// Entre times de mesmo tamanho o resultado ainda passa pelo `applyMinimumChurn`,
// que garante que pelo menos dois jogadores de cada lado mudem de time.
export function rematchTwoTeams(
  teamA: Team,
  teamB: Team,
  options: RematchOptions = {},
): [Team, Team] {
  const rng = options.rng ?? defaultRng;
  // Mesma razão de `balanceTeams`: a redistribuição também insere do mais forte
  // ao mais fraco, e misturar dois times não pode ser o jeito de descobrir a
  // ordem que o sorteio original esconde.
  const [a, b] = shuffleWithinTeams(buildRematch(teamA, teamB, { ...options, rng }), rng);
  return [a, b];
}

function buildRematch(
  teamA: Team,
  teamB: Team,
  options: RematchOptions = {},
): [Team, Team] {
  const rng = options.rng ?? defaultRng;
  const present = [...teamA.players, ...teamB.players];

  // Preserve sizes when both teams have at least one player — that fixes the
  // 5+1 main-vs-overflow case. But if either team is empty (because the user
  // manually removed everyone), fall back to a balanced split so the rematch
  // actually populates both teams.
  const bothNonEmpty = teamA.players.length > 0 && teamB.players.length > 0;
  const caps = bothNonEmpty
    ? [teamA.players.length, teamB.players.length]
    : [Math.ceil(present.length / 2), Math.ceil(present.length / 2)];

  const pair: Team[] = [
    { ...teamA, players: [], totalStars: 0 },
    { ...teamB, players: [], totalStars: 0 },
  ];

  let result: [Team, Team];

  if (options.balanceByGender) {
    const females = present.filter(isFemale);
    const others  = present.filter(p => !isFemale(p));
    const groups  = [females, others].sort((a, b) => a.length - b.length);
    groups.forEach(g => distributeRoundRobinWithCaps(pair, g, caps, rng));
    const optimized = optimizeBalance(pair, 2, true);
    enforceGenderBalance(optimized, 2);
    const finalized = optimizeBalance(optimized, 2, true);
    result = [finalized[0], finalized[1]];
  } else {
    sortByLevelDesc(present, rng).forEach(player => {
      addToTeam(pair, pickBestWithCaps(pair, caps, rng), player);
    });
    result = [pair[0], pair[1]];
  }

  // Só entre times de mesmo tamanho, por decisão sua: quando eles têm tamanhos
  // diferentes quem resolve é o "Completar Time", e forçar movimento ali seria
  // mexer num caso que a tela já trata por outro caminho.
  if (bothNonEmpty && teamA.players.length === teamB.players.length) {
    applyMinimumChurn(result, new Set(teamA.players.map(p => p.id)), !!options.balanceByGender, rng);
  }

  return result;
}

/**
 * Quantos jogadores de cada time têm de trocar de lado para o rebalanceamento
 * ser reconhecido como um.
 *
 * Dois, e não um: uma troca só é indistinguível de uma substituição, e o pedido
 * que originou isto é o de desmontar um time que vinha ganhando.
 */
const MIN_CHURN = 2;

function swapAcross(a: Team, b: Team, i: number, j: number): void {
  const pi = a.players[i];
  const pj = b.players[j];
  a.players[i] = pj;
  b.players[j] = pi;
  a.totalStars = a.totalStars - pi.level + pj.level;
  b.totalStars = b.totalStars - pj.level + pi.level;
}

function starGap(a: Team, b: Team): number {
  return Math.abs(a.totalStars - b.totalStars);
}

/**
 * Garante que o rebalanceamento MOVA gente.
 *
 * ⚠️ **O problema não é aleatoriedade insuficiente — é o algoritmo reencontrar
 * a própria resposta.** O rebalanceamento joga os dois times fora e redistribui
 * do zero, do mais forte ao mais fraco, para quem tem menos estrelas. Só que o
 * par que ele recebe já saiu equilibrado do sorteio original: a mesma regra,
 * sobre a mesma gente, cai quase sempre na mesma divisão. Medido em 500
 * execuções, 16% dos rebalanceamentos de 2×6 mexiam em um jogador ou nenhum, e
 * 3% devolviam os times idênticos. Em 2×5 com gênero eram 41%. Quem clicou viu
 * um botão que não fez nada.
 *
 * Duas fases, nesta ordem:
 *
 * 1. **Subir a contagem até o mínimo**, sempre pela troca que deixa os times
 *    mais perto — em time de cinco ou seis quase sempre existe um par de mesma
 *    nota, e aí o movimento sai de graça.
 * 2. **Otimizar de novo, sem poder desfazer o movimento.** Sem isto a fase 1
 *    seria a última palavra sobre o equilíbrio, e ela escolhe entre as trocas
 *    que aumentam a contagem, não entre todas.
 *
 * Só é chamado com times de mesmo tamanho, então comparar soma crua é a mesma
 * coisa que comparar nota por jogador — a cegueira de tamanho de 08/08 não
 * alcança aqui.
 */
function applyMinimumChurn(
  [a, b]: [Team, Team],
  fromA: Set<string>,
  respectGender: boolean,
  rng: Rng,
): void {
  const target = Math.min(MIN_CHURN, a.players.length);
  // Quantos jogadores do time A vieram do outro lado. Como as trocas são 1×1 e
  // os tamanhos são iguais, o número vale para os dois times.
  const churn = () => a.players.filter(p => !fromA.has(p.id)).length;
  const sameGroup = (pi: Player, pj: Player) => !respectGender || isFemale(pi) === isFemale(pj);
  const gapAfter = (pi: Player, pj: Player) =>
    Math.abs((a.totalStars - pi.level + pj.level) - (b.totalStars - pj.level + pi.level));

  while (churn() < target) {
    // Só o par "quem estava em A" × "quem estava em B" aumenta a contagem: as
    // outras combinações devolvem alguém para casa e a deixam parada.
    const moves: { i: number; j: number; gap: number; tie: number }[] = [];
    a.players.forEach((pi, i) => {
      if (!fromA.has(pi.id)) return;
      b.players.forEach((pj, j) => {
        if (fromA.has(pj.id) || !sameGroup(pi, pj)) return;
        moves.push({ i, j, gap: gapAfter(pi, pj), tie: rng() });
      });
    });
    if (moves.length === 0) break;

    // Chave de desempate sorteada antes de ordenar, e não dentro do comparador —
    // mesma razão do `sortEligible`: comparador inconsistente devolve resultado
    // diferente por motor de JavaScript.
    moves.sort((x, y) => (x.gap !== y.gap ? x.gap - y.gap : x.tie - y.tie));
    swapAcross(a, b, moves[0].i, moves[0].j);
  }

  let improved = true;
  while (improved) {
    improved = false;
    outer:
    for (let i = 0; i < a.players.length; i++) {
      for (let j = 0; j < b.players.length; j++) {
        const pi = a.players[i];
        const pj = b.players[j];
        if (pi.level === pj.level || !sameGroup(pi, pj)) continue;
        // Um trocado que volta para casa derruba a contagem; um que sai de casa
        // pela primeira vez a levanta.
        const delta = (fromA.has(pi.id) ? 0 : -1) + (fromA.has(pj.id) ? 0 : 1);
        if (churn() + delta < target) continue;
        if (gapAfter(pi, pj) < starGap(a, b)) {
          swapAcross(a, b, i, j);
          improved = true;
          break outer;
        }
      }
    }
  }
}

// Local-search post-optimizer: repeatedly swaps players between main teams
// if the swap reduces the spread (max − min) of totalStars. Runs in O(n²)
// passes until convergence.
//
// When respectGender = true, only swap players of the same gender — preserves
// the gender balance produced by the gender-aware distribution.
function optimizeBalance(teams: Team[], numTeams: number, respectGender = false): Team[] {
  const result = teams.map(t => ({ ...t, players: [...t.players] }));

  // `numTeams` é o que foi PEDIDO; `result.length` é o que existe. Os dois
  // divergem para menos quando há menos gente que um time cheio por time
  // pedido — 8 jogadores em "3 times de 5" produz ceil(8/5) = 2 times — e o
  // laço então indexava `result[2]`, undefined, derrubando o sorteio inteiro
  // com um TypeError. Alcançável pela interface: o gate só exige
  // `numTeams × 2` jogadores, então 6 a 10 pessoas pedindo 3 times batiam
  // nisto. Iterar sobre o que existe é o limite correto nos dois sentidos.
  const upTo = Math.min(numTeams, result.length);

  let improved = true;
  while (improved) {
    improved = false;
    outer:
    for (let ti = 0; ti < upTo - 1; ti++) {
      for (let tj = ti + 1; tj < upTo; tj++) {
        for (let pi = 0; pi < result[ti].players.length; pi++) {
          for (let pj = 0; pj < result[tj].players.length; pj++) {
            const pl1 = result[ti].players[pi];
            const pl2 = result[tj].players[pj];
            if (pl1.level === pl2.level) continue;
            // F vs non-F is the only forbidden swap when respecting gender;
            // M ↔ unspecified is free under the "sem-gênero = homem" rule.
            if (respectGender && isFemale(pl1) !== isFemale(pl2)) continue;

            const mainStars = result.slice(0, numTeams).map(t => t.totalStars);
            const currentSpread = Math.max(...mainStars) - Math.min(...mainStars);

            const newI = result[ti].totalStars - pl1.level + pl2.level;
            const newJ = result[tj].totalStars - pl2.level + pl1.level;
            const newStars = mainStars.map((s, k) => k === ti ? newI : k === tj ? newJ : s);
            const newSpread = Math.max(...newStars) - Math.min(...newStars);

            if (newSpread < currentSpread) {
              [result[ti].players[pi], result[tj].players[pj]] =
                [result[tj].players[pj], result[ti].players[pi]];
              result[ti].totalStars = newI;
              result[tj].totalStars = newJ;
              improved = true;
              break outer;
            }
          }
        }
      }
    }
  }

  return result;
}

export interface BalanceOptions {
  balanceByGender?: boolean;
  // Omitir mantém o comportamento antigo (Math.random). Passar um gerador
  // semeado torna o sorteio reproduzível — é o que a verificação da Fatia 9
  // exige, e o que o app web sempre faz.
  rng?: Rng;
}

/**
 * Sorteio balanceado.
 *
 * O corpo do algoritmo é `buildBalancedTeams`; esta função existe para garantir
 * que TODO caminho de saída passe pelo sorteio da ordem interna. Havia três
 * `return` lá dentro, e embaralhar em cada um deixaria o quarto — o que alguém
 * acrescentar depois — vazando a ordem por nível de novo.
 */
export function balanceTeams(
  present: Player[],
  numTeams: number,
  playersPerTeam: number,
  options: BalanceOptions = {},
): Team[] {
  const rng = options.rng ?? defaultRng;
  return shuffleWithinTeams(
    buildBalancedTeams(present, numTeams, playersPerTeam, { ...options, rng }),
    rng,
  );
}

function buildBalancedTeams(
  present: Player[],
  numTeams: number,
  playersPerTeam: number,
  options: BalanceOptions = {},
): Team[] {
  const rng = options.rng ?? defaultRng;
  const maxMainPlayers = numTeams * playersPerTeam;
  const totalTeams = Math.ceil(present.length / playersPerTeam);

  const teams: Team[] = Array.from({ length: totalTeams }, (_, i) => ({
    id: i + 1,
    name: `Time ${i + 1}`,
    players: [],
    totalStars: 0,
  }));

  // Underfilled case: same number of teams the user asked for, but not enough
  // players to fill them all. Default expectation: maximize the number of teams
  // at full cap; whatever's left over goes to the LAST team.
  //   13 players, 3 teams of 5 → caps [5, 5, 3] (not [5, 4, 4]).
  // Stars are still distributed round-robin so the short team doesn't end up
  // weak. The post-optimizer only touches teams that share the same cap, so it
  // never pulls a player out of the short team to "equalize" star sums.
  const underfilled = totalTeams === numTeams && present.length < maxMainPlayers;
  let perTeamCaps: number[] | null = null;
  if (underfilled) {
    perTeamCaps = [];
    let remaining = present.length;
    for (let i = 0; i < numTeams; i++) {
      const slot = Math.min(remaining, playersPerTeam);
      perTeamCaps.push(slot);
      remaining -= slot;
    }
  }
  const numFullTeams = perTeamCaps
    ? perTeamCaps.filter(c => c === playersPerTeam).length
    : numTeams;

  if (options.balanceByGender) {
    // Strict round-robin per gender: each team gets one player from a gender
    // per round before any team gets a second. Guarantees the count of each
    // gender differs by at most 1 across teams, no matter how lopsided the
    // star totals are after men are placed.
    if (perTeamCaps) {
      // Underfilled: same per-team caps logic, but split by F vs non-F to keep
      // gender proportion across teams of equal cap.
      const females = present.filter(isFemale);
      const others = present.filter(p => !isFemale(p));
      const groups = [females, others].sort((a, b) => a.length - b.length);
      groups.forEach(g => distributeRoundRobinWithCaps(teams, g, perTeamCaps!, rng));
    } else {
      applyGenderRoundRobin(teams, present, numTeams, playersPerTeam, rng);
    }
    const balanced = optimizeBalance(teams, numFullTeams, true);
    enforceGenderBalance(balanced, numFullTeams);
    // One last star pass after the safety swaps — gender count is now locked
    // and the optimizer can shuffle same-gender players to tighten the spread.
    return optimizeBalance(balanced, numFullTeams, true);
  }

  if (perTeamCaps) {
    // Round-robin with per-team caps fills the short team in the first rounds
    // and then continues with the full-cap teams — guarantees the short team
    // gets a mix of strong/weak players rather than only leftovers.
    distributeRoundRobinWithCaps(teams, present, perTeamCaps, rng);
    return optimizeBalance(teams, numFullTeams);
  }

  // Shuffle the full list so overflow candidates are chosen at random —
  // anyone can end up in the sobra team, regardless of their level.
  const randomised = shuffle(present, rng);
  const overflowPlayers = randomised.slice(maxMainPlayers);
  const mainPlayers    = randomised.slice(0, maxMainPlayers);

  // Pre-fill the overflow team (last slot) with the randomly chosen players.
  overflowPlayers.forEach(p => addToTeam(teams, totalTeams - 1, p));

  // Distribute the remaining players across the main teams using the
  // strongest-first greedy so star sums stay as balanced as possible.
  sortByLevelDesc(mainPlayers, rng).forEach(player => {
    addToTeam(teams, getEligibleTeam(teams, numTeams, playersPerTeam, rng), player);
  });

  return optimizeBalance(teams, numTeams);
}

// Rebuilds the totalStars of every team from its current player list.
// Used after add/remove edits in the Teams screen.
export function recalcTeams(teams: Team[]): Team[] {
  return teams.map(t => {
    const next = { ...t, players: [...t.players] };
    recomputeStars(next);
    return next;
  });
}

// Picks N players uniformly at random from a pool. Used by the "Sorteio
// Aleatório" mode of Completar Time. If n >= pool.length, returns the whole
// pool shuffled (fail-soft for the UI).
export function pickRandomN(pool: Player[], n: number, rng: Rng = defaultRng): Player[] {
  if (n <= 0) return [];
  return shuffle(pool, rng).slice(0, n);
}

// Enumerates all C(pool.length, n) subsets and returns the one whose summed
// level is closest to `targetSum`. Ties broken by shuffling the candidate set
// so repeated runs don't always pick the same player. O(C(p,n)) — bounded by
// the caller (UI gates on small N missing, small source teams).
function bestCombinationByTarget(
  pool: Player[],
  n: number,
  targetSum: number,
  rng: Rng,
): Player[] {
  const best: { combo: Player[]; dist: number }[] = [];
  const current: Player[] = [];

  function recurse(start: number, depth: number, sumSoFar: number) {
    if (depth === n) {
      const dist = Math.abs(sumSoFar - targetSum);
      if (best.length === 0 || dist < best[0].dist) {
        best.length = 0;
        best.push({ combo: [...current], dist });
      } else if (dist === best[0].dist) {
        best.push({ combo: [...current], dist });
      }
      return;
    }
    const remaining = n - depth;
    for (let i = start; i <= pool.length - remaining; i++) {
      current.push(pool[i]);
      recurse(i + 1, depth + 1, sumSoFar + pool[i].level);
      current.pop();
    }
  }

  recurse(0, 0, 0);
  if (best.length === 0) return [];
  const picked = best[Math.floor(rng() * best.length)];
  return picked.combo;
}

// Greedy fallback for "balanced" pick when the exhaustive search would explode
// (large pools / large N). Picks players one at a time, each time choosing the
// player whose level brings the running sum closest to `targetSum`.
function greedyByTarget(pool: Player[], n: number, targetSum: number): Player[] {
  const remaining = [...pool];
  const chosen: Player[] = [];
  let runningSum = 0;
  for (let k = 0; k < n && remaining.length > 0; k++) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const dist = Math.abs(runningSum + remaining[i].level - targetSum);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    const [picked] = remaining.splice(bestIdx, 1);
    chosen.push(picked);
    runningSum += picked.level;
  }
  return chosen;
}

// Picks N players from `pool` whose summed levels best approach `targetSum`.
// Used by the "Sorteio Balanceado" mode of Completar Time, where targetSum is
// the gap the incomplete team needs to close to match the average of the
// complete teams.
//
// Uses exhaustive enumeration when feasible (pool ≤ 12 and n ≤ 4 ⇒ ≤ 495
// combinations) and greedy otherwise.
export function pickBalancedN(
  pool: Player[],
  n: number,
  targetSum: number,
  rng: Rng = defaultRng,
): Player[] {
  if (n <= 0) return [];
  if (n >= pool.length) return [...pool];
  if (pool.length <= 12 && n <= 4) {
    return bestCombinationByTarget(pool, n, targetSum, rng);
  }
  return greedyByTarget(pool, n, targetSum);
}

/**
 * Quantas mulheres há neste grupo.
 *
 * Exportado, e não recalculado em quem precisa, porque a regra "sem gênero
 * conta como homem" mora no `isFemale` logo acima e vale para o produto
 * inteiro. Uma segunda cópia dela em outro arquivo divergiria no dia em que
 * aparecesse um terceiro valor de gênero — e divergiria em silêncio, com os
 * dois lados achando que concordam.
 */
export function countFemales(players: Player[]): number {
  return players.filter(isFemale).length;
}

/**
 * Escolhe N do doador com o GÊNERO decidindo antes das estrelas.
 *
 * É o "Completar Time" de uma pelada sorteada com equilíbrio por gênero. Quem
 * ligou aquela opção pediu times parecidos em composição, e um time com três
 * mulheres contra um com uma se nota da beira do campo — a diferença de nota
 * que essa prioridade custa, não.
 *
 * ⚠️ O gênero é decidido como uma CONTAGEM, e não como desempate entre
 * combinações. Desempatar não resolve: as combinações empatadas em estrelas são
 * raras (com 20 candidatas o melhor alvo costuma ser único), e medindo os dois
 * o desempate saía em 1,07 mulher de desvio contra 0,36 desta versão.
 *
 * Fixada a contagem, as estrelas voltam a mandar dentro de cada grupo. Os
 * homens são escolhidos DEPOIS das mulheres, contra o que sobrou do alvo: assim
 * o segundo grupo absorve o que o primeiro não conseguiu acertar, em vez de os
 * dois errarem para o mesmo lado.
 *
 * `wantedFemales` é quantas mulheres tirar do doador, e vem de quem chama — só
 * ele sabe com que times o alvo está sendo comparado. Aqui ela é apenas
 * aparada para o que o doador tem.
 */
export function pickBalancedNByGender(
  pool: Player[],
  n: number,
  targetSum: number,
  wantedFemales: number,
  rng: Rng = defaultRng,
): Player[] {
  if (n <= 0) return [];
  if (n >= pool.length) return [...pool];

  const females = pool.filter(isFemale);
  const males = pool.filter(p => !isFemale(p));

  // O piso não é zero: se não houver homens suficientes para completar, a
  // diferença tem de sair das mulheres de qualquer forma. Com `n < pool.length`
  // garantido acima, piso e teto nunca se cruzam.
  const wanted = Math.min(
    Math.min(n, females.length),
    Math.max(Math.max(0, n - males.length), Math.round(wantedFemales)),
  );

  // Alvo proporcional para o primeiro grupo. É estimativa, e é de propósito:
  // quem fecha a conta é a segunda chamada, que recebe o resto exato.
  const chosenF = pickBalancedN(females, wanted, (targetSum * wanted) / n, rng);
  const takenF = chosenF.reduce((s, p) => s + p.level, 0);
  const chosenM = pickBalancedN(males, n - wanted, targetSum - takenF, rng);

  return [...chosenF, ...chosenM];
}

// Sorts a pool by |level - targetLevel| ascending. Same-distance players are
// shuffled so the UI doesn't always show the same first match. Used by the
// Injury Substitution flow to surface "same-level" subs at the top of the list.
export function pickByLevelProximity(
  pool: Player[],
  targetLevel: number,
  rng: Rng = defaultRng,
): Player[] {
  const groups = new Map<number, Player[]>();
  pool.forEach(p => {
    const d = Math.abs(p.level - targetLevel);
    const arr = groups.get(d);
    if (arr) arr.push(p);
    else groups.set(d, [p]);
  });
  const distances = [...groups.keys()].sort((a, b) => a - b);
  return distances.flatMap(d => shuffle(groups.get(d)!, rng));
}

/**
 * Nível médio do time, na mesma escala do jogador (0,5 a 5).
 *
 * A soma das estrelas é o que o balanceador equaliza, e por isso ela é o que
 * fica guardado. Mas ela NÃO é um nível: um time de cinco somando 16 não tem
 * "nível 16" quando o máximo de uma pessoa é 5, e mostrar aquele número ao lado
 * da escala do jogador não fecha para quem lê.
 *
 * A média fecha: 3,2 se compara de imediato com o 4,5 de alguém do elenco. E
 * continua comparável quando os times têm tamanhos diferentes — o que acontece
 * sempre que o elenco não divide certo —, situação em que a soma compararia
 * coisas distintas e faria o time menor parecer mais fraco.
 *
 * Uma casa decimal: a entrada vem em passos de meia estrela e a média de um
 * punhado deles não merece mais precisão do que isso.
 */
export function averageLevel(totalStars: number, playerCount: number): number {
  if (playerCount <= 0) return 0;
  return Math.round((totalStars / playerCount) * 10) / 10;
}
